import { LISTING_STATES } from '../types';
import ActiveChipsRow from './ActiveChipsRow';
import CheckboxList from './CheckboxList';
import FilterDropdown from './FilterDropdown';
import RangeRow from './RangeRow';
import {
  INSERATE_STATE_ORDER,
  ZUORDNUNG_ROWS,
  type InserateFilter,
  type SelectedVermieter,
  type ZuordnungValue,
} from '../types/inserateFilter';
import type { UseInserateFilterResult } from '../hooks/useInserateFilter';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface InserateFilterBarProps
  extends Pick<
    UseInserateFilterResult,
    | 'filter'
    | 'setFilter'
    | 'setFilterState'
    | 'resetFilter'
    | 'toggleVermieter'
    | 'vermieterSearch'
    | 'setVermieterSearch'
    | 'vermieterList'
    | 'selectedVermieterIds'
    | 'activeChips'
    | 'badgeCounts'
    | 'filterActive'
  > {
  stats?: { by_state?: Record<string, number> } | null;
  platformOptions: string[];
  listingTypeOptions: string[];
  /** Wird bei jeder Filter-Änderung aufgerufen – z. B. zum Zurücksetzen der Seite */
  onAnyChange?: () => void;
  /** Überschreibt die CSS-Klasse des äußeren Containers (Standard: Karte für Listenseiten) */
  wrapperClassName?: string;
}

// ─── Haupt-Komponente ─────────────────────────────────────────────────────────

export default function InserateFilterBar({
  filter,
  setFilter,
  setFilterState,
  resetFilter,
  toggleVermieter,
  vermieterSearch,
  setVermieterSearch,
  vermieterList,
  selectedVermieterIds,
  activeChips,
  badgeCounts,
  filterActive,
  stats,
  platformOptions,
  listingTypeOptions,
  onAnyChange,
  wrapperClassName,
}: InserateFilterBarProps) {
  // Wrapper: Filter setzen + optional Seite zurücksetzen
  const change = <K extends keyof InserateFilter>(key: K, val: InserateFilter[K]) => {
    setFilter(key, val);
    onAnyChange?.();
  };

  const changeState = (updater: InserateFilter | ((prev: InserateFilter) => InserateFilter)) => {
    setFilterState(updater);
    onAnyChange?.();
  };

  const handleToggleVermieter = (v: SelectedVermieter) => {
    toggleVermieter(v);
    onAnyChange?.();
  };

  const handleReset = () => {
    resetFilter();
    onAnyChange?.();
  };

  const bookedTotalNights =
    filter.bookedFrom && filter.bookedTo
      ? Math.round(
          (new Date(filter.bookedTo).getTime() - new Date(filter.bookedFrom).getTime()) /
            86400000,
        )
      : null;

  return (
    <div className={wrapperClassName ?? 'bg-white rounded-lg shadow p-4 mb-4 space-y-3'}>

      {/* Zeile 1: Filter-Dropdowns */}
      <div className="flex items-center gap-2 flex-wrap">

        {/* Status */}
        <FilterDropdown label="Status" badgeCount={badgeCounts.statusBadge}>
          <div className="flex rounded-lg border border-gray-200 overflow-hidden mb-3">
            {(['include', 'exclude'] as const).map(mode => (
              <button
                key={mode}
                onClick={() => change('stateMode', mode)}
                className={`flex-1 py-1.5 text-xs font-medium transition-colors ${
                  filter.stateMode === mode
                    ? 'bg-gray-900 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {mode === 'include' ? 'Einschließen' : 'Ausschließen'}
              </button>
            ))}
          </div>
          <div className="flex gap-3 mb-2 px-1">
            <button
              onClick={() => change('states', [...INSERATE_STATE_ORDER])}
              className="text-xs text-blue-600 hover:underline"
            >
              Alle
            </button>
            <button
              onClick={() => change('states', [])}
              className="text-xs text-blue-600 hover:underline"
            >
              Keine
            </button>
          </div>
          <div className="space-y-0.5">
            {INSERATE_STATE_ORDER.map(s => {
              const info = LISTING_STATES[s];
              const count = stats?.by_state?.[s] || 0;
              const checked = filter.states.includes(s);
              return (
                <label
                  key={s}
                  className="flex items-center gap-2.5 py-1.5 px-1 cursor-pointer hover:bg-gray-50 rounded-lg"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() =>
                      change(
                        'states',
                        checked
                          ? filter.states.filter(x => x !== s)
                          : [...filter.states, s],
                      )
                    }
                    className="rounded border-gray-300 text-gray-900"
                  />
                  <span className="text-sm flex-1">{info.label}</span>
                  <span className="text-xs text-gray-400 tabular-nums">{count}</span>
                </label>
              );
            })}
          </div>
        </FilterDropdown>

        {/* Online-Status */}
        <FilterDropdown label="Online" badgeCount={badgeCounts.onlineBadge}>
          <p className="text-xs text-gray-400 mb-2.5 px-1">Online-Status des Inserats</p>
          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
            {(['alle', 'online', 'offline'] as const).map(val => (
              <button
                key={val}
                onClick={() => change('isOnline', val)}
                className={`flex-1 px-2.5 py-1.5 text-xs font-medium transition-colors ${
                  filter.isOnline === val
                    ? 'bg-gray-900 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {val === 'alle' ? 'Alle' : val === 'online' ? 'Nur online' : 'Nur offline'}
              </button>
            ))}
          </div>
        </FilterDropdown>

        {/* Vermieter */}
        <FilterDropdown label="Vermieter" badgeCount={badgeCounts.vermieterBadge}>
          <input
            type="text"
            placeholder="Name suchen…"
            value={vermieterSearch}
            onChange={e => setVermieterSearch(e.target.value)}
            className="w-full border rounded-lg px-2.5 py-1.5 text-sm mb-2"
            autoFocus
          />
          {vermieterList.length === 0 ? (
            <p className="text-xs text-gray-400 px-1 py-2 text-center">
              Keine Vermieter gefunden
            </p>
          ) : (
            <div className="space-y-0.5 max-h-56 overflow-y-auto">
              {vermieterList.map(v => (
                <label
                  key={v.id}
                  className="flex items-center gap-2.5 py-1.5 px-1 cursor-pointer hover:bg-gray-50 rounded-lg"
                >
                  <input
                    type="checkbox"
                    checked={selectedVermieterIds.has(v.id)}
                    onChange={() => handleToggleVermieter(v)}
                    className="rounded border-gray-300 text-gray-900"
                  />
                  <span className="text-sm flex-1 truncate">{v.name}</span>
                </label>
              ))}
            </div>
          )}
          {filter.selectedVermieter.length > 0 && (
            <button
              onClick={() => change('selectedVermieter', [])}
              className="mt-2 w-full text-xs text-red-500 hover:text-red-700 text-center"
            >
              Auswahl aufheben
            </button>
          )}
        </FilterDropdown>

        {/* Lage */}
        <FilterDropdown label="Lage" badgeCount={badgeCounts.lageBadge}>
          <div className="space-y-3">
            <div>
              <p className="text-xs text-gray-500 mb-1">Straße / Hausnr.</p>
              <input
                type="text"
                placeholder="z. B. Hauptstraße 5"
                value={filter.addressSearch}
                onChange={e => change('addressSearch', e.target.value)}
                className="w-full border rounded-lg px-2.5 py-1.5 text-sm"
              />
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">PLZ</p>
              <input
                type="text"
                placeholder="z. B. 10115"
                value={filter.zipcodeSearch}
                onChange={e => change('zipcodeSearch', e.target.value)}
                className="w-full border rounded-lg px-2.5 py-1.5 text-sm"
              />
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Stadt</p>
              <input
                type="text"
                placeholder="z. B. Berlin"
                value={filter.citySearch}
                onChange={e => change('citySearch', e.target.value)}
                className="w-full border rounded-lg px-2.5 py-1.5 text-sm"
              />
            </div>
          </div>
        </FilterDropdown>

        {/* Objekt */}
        <FilterDropdown label="Objekt" badgeCount={badgeCounts.objektBadge}>
          <div className="space-y-4 max-h-96 overflow-y-auto pr-1">
            {platformOptions.length > 0 && (
              <div>
                <p className="text-xs text-gray-500 mb-1.5">Plattform</p>
                <CheckboxList
                  options={platformOptions}
                  selected={filter.selectedPlatforms}
                  onToggle={v => {
                    changeState(f => ({
                      ...f,
                      selectedPlatforms: f.selectedPlatforms.includes(v)
                        ? f.selectedPlatforms.filter(x => x !== v)
                        : [...f.selectedPlatforms, v],
                    }));
                  }}
                />
              </div>
            )}
            {listingTypeOptions.length > 0 && (
              <div>
                <p className="text-xs text-gray-500 mb-1.5">Typ</p>
                <CheckboxList
                  options={listingTypeOptions}
                  selected={filter.selectedListingTypes}
                  onToggle={v => {
                    changeState(f => ({
                      ...f,
                      selectedListingTypes: f.selectedListingTypes.includes(v)
                        ? f.selectedListingTypes.filter(x => x !== v)
                        : [...f.selectedListingTypes, v],
                    }));
                  }}
                />
              </div>
            )}
            <RangeRow
              label="Preis pro Nacht"
              unit="€"
              minVal={filter.priceMin}
              maxVal={filter.priceMax}
              onChange={(min, max) =>
                changeState(f => ({ ...f, priceMin: min, priceMax: max }))
              }
            />
            <RangeRow
              label="Bewertung"
              minPlaceholder="1"
              maxPlaceholder="5"
              minVal={filter.ratingMin}
              maxVal={filter.ratingMax}
              onChange={(min, max) =>
                changeState(f => ({ ...f, ratingMin: min, ratingMax: max }))
              }
            />
            <RangeRow
              label="Größe"
              unit="m²"
              minVal={filter.sizeMin}
              maxVal={filter.sizeMax}
              onChange={(min, max) =>
                changeState(f => ({ ...f, sizeMin: min, sizeMax: max }))
              }
            />
            <RangeRow
              label="Gäste"
              minVal={filter.guestsMin}
              maxVal={filter.guestsMax}
              onChange={(min, max) =>
                changeState(f => ({ ...f, guestsMin: min, guestsMax: max }))
              }
            />
            <RangeRow
              label="Schlafzimmer"
              minVal={filter.bedroomsMin}
              maxVal={filter.bedroomsMax}
              onChange={(min, max) =>
                changeState(f => ({ ...f, bedroomsMin: min, bedroomsMax: max }))
              }
            />
          </div>
        </FilterDropdown>

        {/* Buchungen */}
        <FilterDropdown label="Buchungen" badgeCount={badgeCounts.buchungenBadge}>
          <p className="text-xs text-gray-400 mb-3">
            Inserate mit X gebuchten Nächten im gewählten Zeitraum
          </p>
          <div className="space-y-3">
            <div>
              <p className="text-xs text-gray-500 mb-1">Zeitraum von</p>
              <input
                type="date"
                value={filter.bookedFrom}
                onChange={e => change('bookedFrom', e.target.value)}
                className="w-full border rounded-lg px-2.5 py-1.5 text-sm"
              />
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Zeitraum bis</p>
              <input
                type="date"
                value={filter.bookedTo}
                min={filter.bookedFrom || undefined}
                onChange={e => change('bookedTo', e.target.value)}
                className="w-full border rounded-lg px-2.5 py-1.5 text-sm"
              />
            </div>
            {bookedTotalNights !== null && bookedTotalNights > 0 && (
              <p className="text-xs text-gray-300 text-right -mt-1">
                {bookedTotalNights} Nächte gesamt
              </p>
            )}
            <div>
              <p className="text-xs text-gray-500 mb-1">Gebuchte Nächte</p>
              <div className="flex items-center gap-1.5">
                <input
                  type="number"
                  min="0"
                  placeholder="von"
                  value={filter.bookedMinNights}
                  onChange={e => change('bookedMinNights', e.target.value)}
                  className="w-full border rounded-lg px-2.5 py-1.5 text-sm"
                />
                <span className="text-gray-400 text-xs shrink-0">–</span>
                <input
                  type="number"
                  min="0"
                  placeholder="bis"
                  value={filter.bookedMaxNights}
                  onChange={e => change('bookedMaxNights', e.target.value)}
                  className="w-full border rounded-lg px-2.5 py-1.5 text-sm"
                />
              </div>
            </div>
            {(filter.bookedFrom ||
              filter.bookedTo ||
              filter.bookedMinNights ||
              filter.bookedMaxNights) && (
              <button
                onClick={() =>
                  changeState(f => ({
                    ...f,
                    bookedFrom: '',
                    bookedTo: '',
                    bookedMinNights: '',
                    bookedMaxNights: '',
                  }))
                }
                className="w-full text-xs text-red-500 hover:text-red-700 text-center"
              >
                Zurücksetzen
              </button>
            )}
          </div>
        </FilterDropdown>

        {/* Zuordnung */}
        <FilterDropdown label="Zuordnung" badgeCount={badgeCounts.zuordnungBadge}>
          <p className="text-xs text-gray-400 mb-2.5 px-1">Inserat hat … zugeordnet</p>
          <div className="space-y-1.5">
            {ZUORDNUNG_ROWS.map(({ key, label }) => {
              const current = filter[key] as ZuordnungValue;
              return (
                <div key={key} className="flex items-center justify-between gap-3">
                  <span className="text-sm text-gray-700 w-28 shrink-0">{label}</span>
                  <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                    {(['any', 'has', 'missing'] as ZuordnungValue[]).map(val => (
                      <button
                        key={val}
                        onClick={() =>
                          changeState(f => ({ ...f, [key]: val }))
                        }
                        className={`px-2.5 py-1 text-xs font-medium transition-colors ${
                          current === val
                            ? 'bg-gray-900 text-white'
                            : 'bg-white text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        {val === 'any' ? 'Alle' : val === 'has' ? 'Hat' : 'Hat nicht'}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </FilterDropdown>

        {/* Reset */}
        {filterActive && (
          <button
            onClick={handleReset}
            className="text-sm text-red-500 hover:text-red-700 transition-colors"
          >
            Filter zurücksetzen
          </button>
        )}
      </div>

      {/* Zeile 2: Aktive Filter-Chips */}
      <ActiveChipsRow chips={activeChips} />
    </div>
  );
}
